import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";

const STEPS = [
  { key: "company_size", title: "Taille de votre équipe", description: "Aide-nous à adapter les recommandations." },
  { key: "industry", title: "Votre secteur", description: "Pour prioriser les templates adaptés." },
  { key: "main_goal", title: "Objectif principal", description: "Qu'aimeriez-vous automatiser en premier ?" },
];

const SIZES = ["1-9", "10-49", "50-99", "100-200", "200+"];
const INDUSTRIES = ["SaaS / Tech", "E-commerce", "Services B2B", "Industrie", "Santé", "Finance", "Autre"];
const GOALS = [
  "Gagner du temps sur les tâches administratives",
  "Automatiser le support client",
  "Synchroniser mes outils (Gmail, Slack, Notion…)",
  "Suivre mes KPIs plus efficacement",
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ company_size: "", industry: "", main_goal: "" });
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const currentKey = STEPS[step].key;
  const canProceed = !!data[currentKey];

  const next = async () => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/onboarding", data);
      await refresh();
      toast.success("Configuration terminée !");
      navigate("/app/dashboard");
    } catch {
      toast.error("Impossible de finaliser l'onboarding.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-3 flex-1">
              <div
                className={`w-8 h-8 rounded-sm flex items-center justify-center font-mono text-xs ${
                  i < step ? "klein-bg text-white" : i === step ? "bg-black text-white" : "bg-zinc-200 text-zinc-500"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "klein-bg" : "bg-zinc-200"}`}></div>}
            </div>
          ))}
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-10" data-testid={`onboarding-step-${step}`}>
          <span className="overline">Étape {step + 1} sur {STEPS.length}</span>
          <h2 className="font-display text-3xl font-medium tracking-tight mt-3 mb-2">{STEPS[step].title}</h2>
          <p className="text-zinc-600 mb-8">{STEPS[step].description}</p>

          {currentKey === "company_size" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SIZES.map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => setData({ ...data, company_size: s })}
                  className={`py-4 border rounded-sm text-sm transition-all ${
                    data.company_size === s ? "border-black bg-black text-white" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`onboarding-size-${s}`}
                >
                  {s} salariés
                </button>
              ))}
            </div>
          )}

          {currentKey === "industry" && (
            <div className="grid grid-cols-2 gap-3">
              {INDUSTRIES.map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => setData({ ...data, industry: s })}
                  className={`py-4 border rounded-sm text-sm transition-all ${
                    data.industry === s ? "border-black bg-black text-white" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`onboarding-industry-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {currentKey === "main_goal" && (
            <div className="space-y-3">
              {GOALS.map((g) => (
                <button
                  key={g} type="button"
                  onClick={() => setData({ ...data, main_goal: g })}
                  className={`w-full text-left py-4 px-5 border rounded-sm text-sm transition-all ${
                    data.main_goal === g ? "border-black bg-black text-white" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`onboarding-goal-${GOALS.indexOf(g)}`}
                >
                  {g}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-10">
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="text-sm text-zinc-500 hover:text-black"
                data-testid="onboarding-prev-button"
              >
                ← Précédent
              </button>
            ) : <span></span>}
            <Button
              onClick={next}
              disabled={!canProceed || saving}
              className="rounded-sm bg-black text-white hover:bg-zinc-800"
              data-testid="onboarding-next-button"
            >
              {step === STEPS.length - 1 ? (saving ? "Finalisation..." : "Finaliser") : "Continuer"} <ArrowRight size={14} className="ml-2"/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
